import { Package } from '@features/DynamicPackagesConfigurationWizard/types/Package.schema';
import { fitsPackageStayLength } from '@features/packages/utils/fitsPackageStayLength';
import { Rate } from '@features/rates/types';
import { FIVE_MINUTES } from '@features/state/QueryClient/QueryClient';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { useQuery } from '@tanstack/react-query';
import { isFailedRequest } from '@utils/isFailedRequest';
import moment from 'moment';
import { Property } from 'shared-types/Property';
import { TimeSlotsType } from 'shared-types/SpaPackage';
import { getSpaPackageTimeslots } from './useGetSpaPackageTimeslots';

export const PackagesSpaAvailabilityKey = {
  packagesSpaAvailability: ['packagesSpaAvailability'] as const,
  getPackagesSpaAvailability: (
    propertyId: number,
    rates: string[],
    journeyParams: ParsedJourneyParams
  ) =>
    [
      ...PackagesSpaAvailabilityKey.packagesSpaAvailability,
      propertyId,
      rates,
      journeyParams,
    ] as const,
};

export const usePackagesSpaAvailability = (
  property: Property,
  rates: Rate[],
  journeyParams: ParsedJourneyParams,
  enabled = true
) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: PackagesSpaAvailabilityKey.getPackagesSpaAvailability(
      property.id,
      rates.map((rate) => rate.id),
      journeyParams
    ),
    queryFn: () => getPackagesSpaAvailability(property, rates, journeyParams),
    staleTime: FIVE_MINUTES,
    cacheTime: FIVE_MINUTES,
    enabled,
  });
};

export const getPackagesSpaAvailability = async (
  property: Property,
  rates: Rate[],
  journeyParams: ParsedJourneyParams
) => {
  const numberOfGuests = journeyParams.adults + journeyParams.children;

  const newAvailability = await Promise.all(
    rates.map(async (rate) => {
      if (rate.unAvailable) return null;

      const packageData: Package | undefined = rate.package ?? undefined;

      if (!packageData) return null;

      let timeslots: TimeSlotsType = {};
      let isUnavailable = false;

      const spaPackageId = Number(packageData.meta?.entityId);
      if (!spaPackageId) {
        return { rate, packageData, timeslots, isUnavailable };
      }

      const numberOfNights = moment(journeyParams.toDate).diff(
        moment(journeyParams.fromDate),
        'days'
      );
      if (!fitsPackageStayLength(packageData, numberOfNights)) {
        return { rate, packageData, timeslots, isUnavailable: true };
      }

      try {
        timeslots = await getSpaPackageTimeslots({
          spaPackageId,
          startDate: moment(journeyParams.fromDate).format('YYYY-MM-DD'),
          quantity: numberOfGuests,
          propertyId: property.id,
          numberOfDays: moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days'),
        });

        const timeslot = Object.values(timeslots || [])?.[0]?.[0];
        isUnavailable = timeslot === undefined;
      } catch (error) {
        if (isFailedRequest(error)) {
          return { rate, packageData, timeslots, isUnavailable: true, isCheckFailed: true };
        }
        isUnavailable = true;
      }
      return { rate, packageData, timeslots, isUnavailable };
    })
  );
  return newAvailability.reduce((acc: Record<string, (typeof newAvailability)[number]>, data) => {
    if (data) acc[data.rate.id] = data;
    return acc;
  }, {});
};
